import { useRef, useState } from 'react';
import { Box, Button, CircularProgress, Stack } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { OrderCompositionCard, OrderDetailsCard } from '../../components';
import { SubHeader } from '@/components';
import { getContentLanguage, onQueryError, onQuerySuccess, useOrder } from '@/core';
import { ORDERS_ROUTE } from '@/routes';
import palette from '@/theme/palette';

// The receipt HTML response carries its own stylesheet as a <link> tag. Setting innerHTML
// to a full document string doesn't insert that <link> into the real document head (browsers
// may still speculatively fetch it, which is why it can appear to load in devtools without
// ever applying), so we pull it out and attach it ourselves before printing.
const receiptStylesheetId = 'receipt-print-stylesheet';

const loadReceiptStylesheet = (href: string | null | undefined) => {
  if (!href) return Promise.resolve();

  const existing = document.getElementById(receiptStylesheetId);
  if (existing instanceof HTMLLinkElement && existing.href === href) return Promise.resolve();
  existing?.remove();

  return new Promise<void>((resolve) => {
    const link = document.createElement('link');
    link.id = receiptStylesheetId;
    link.rel = 'stylesheet';
    link.href = href;
    link.onload = () => resolve();
    link.onerror = () => resolve(); // don't block printing if the stylesheet fails to load
    document.head.appendChild(link);
  });
};

export const PageOrderDetails = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { useOrderQuery, updateStatusMutation, printReceiptMutation } = useOrder();
  const queryClient = useQueryClient();
  const [isCancelOrderLoading, setIsCancelOrderLoading] = useState(false);
  const [isPrintReceiptLoading, setIsPrintReceiptLoading] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  const { data: order, isLoading, isError } = useOrderQuery(Number(id));

  if (isLoading) return <CircularProgress sx={{ margin: 'auto', mt: '40dvh' }} />;
  if (isError || !order || order.status !== 200 || !order.data)
    return <div>{t('ORDER.LOADING_FAILURE')}</div>;

  const orderDetails = order.data;
  const isOrderCancelled = orderDetails.status === 'cancelled';

  const handleCancelOrderClick = async () => {
    setIsCancelOrderLoading(true);
    const updateResponse = await updateStatusMutation.mutateAsync({
      id: Number(id),
      status: 'cancelled',
    });
    if (updateResponse.status === 200 && updateResponse.data) {
      onQuerySuccess({
        queryKeysToInvalidate: [['order', Number(id)]],
        queryClient,
      });
    } else {
      onQueryError({ errorMessage: t('ORDER.CANCEL_FAILURE') });
    }
    setIsCancelOrderLoading(false);
  };

  const handlePrintReceiptClick = async () => {
    setIsPrintReceiptLoading(true);
    try {
      const response = await printReceiptMutation.mutateAsync({
        id: Number(id),
        lang: getContentLanguage(i18n.language),
      });
      if (response.status === 200 && response.data && printRef.current) {
        const parsedReceipt = new DOMParser().parseFromString(response.data, 'text/html');
        const stylesheetHref = parsedReceipt
          .querySelector('link[rel="stylesheet"]')
          ?.getAttribute('href');

        await loadReceiptStylesheet(stylesheetHref);

        printRef.current.innerHTML = parsedReceipt.body.innerHTML;
        window.print();
      } else {
        onQueryError({ errorMessage: t('ORDER.PRINT_RECEIPT_FAILURE') });
      }
    } catch {
      onQueryError({ errorMessage: t('ORDER.PRINT_RECEIPT_FAILURE') });
    }
    setIsPrintReceiptLoading(false);
  };

  return (
    <Box width="100%">
      <SubHeader title={t('ORDER.ORDER')} backPath={ORDERS_ROUTE}>
        <Stack direction="row" gap={2}>
          <Button
            variant="outlined"
            onClick={handleCancelOrderClick}
            disabled={isOrderCancelled || isLoading || isCancelOrderLoading}
          >
            {isOrderCancelled ? t('ORDER.CANCELLED') : t('ORDER.CANCEL')}
          </Button>
          <Button
            variant="outlined"
            onClick={handlePrintReceiptClick}
            disabled={isPrintReceiptLoading}
          >
            {t('ORDER.PRINT_RECEIPT')}
          </Button>
        </Stack>
      </SubHeader>
      <Box width="100%" sx={{ backgroundColor: palette.surface.lightest }}>
        <Stack
          direction="column"
          gap={8}
          minHeight="100dvh"
          py={4}
          px={7}
          pr={{ xs: 2, xxl: 8 }}
          sx={{ maxWidth: '1700px' }}
        >
          <OrderDetailsCard order={orderDetails} />
          <OrderCompositionCard order={orderDetails} />
        </Stack>
      </Box>
      {/*
        Rendered via a portal directly onto <body> (a sibling of #root, not nested inside it),
        so that hiding #root for print doesn't also hide this — and so its layout height isn't
        inflated by the rest of the (hidden) app, which was producing an extra blank page.
      */}
      {createPortal(<div id="printDiv" ref={printRef} />, document.body)}
    </Box>
  );
};
